import type { TFunction } from "i18next"

import { localizeDuration } from "./localized-summary"

// Readable choices for the two durations the builder edits. A preset only
// fills the parts (days/hours/minutes); the parts stay the source of truth, so
// a stored value that matches no preset opens as Custom with its exact parts.

export type DelayPreset = "15m" | "1h" | "1d" | "3d" | "custom"
export type CadencePreset = "1d" | "2d" | "1w" | "custom"

export const CUSTOM = "custom" as const

/** A week is 7 days; the wire format has no week unit, so weeks travel as hours or minutes. */
export const HOURS_PER_WEEK = 7 * 24

export const DELAY_PRESETS: { value: Exclude<DelayPreset, "custom">; minutes: number }[] = [
  { value: "15m", minutes: 15 },
  { value: "1h", minutes: 60 },
  { value: "1d", minutes: 1440 },
  { value: "3d", minutes: 4320 }
]

export const CADENCE_PRESETS: { value: Exclude<CadencePreset, "custom">; hours: number }[] = [
  { value: "1d", hours: 24 },
  { value: "2d", hours: 48 },
  { value: "1w", hours: HOURS_PER_WEEK }
]


export const delayPresetOptions = (t: TFunction) =>
  DELAY_PRESETS.map(({ value, minutes }) => ({
    value,
    label: localizeDuration(t, minutes, "minutes")
  }))

// A whole number of weeks reads as weeks, which the compound duration has no unit for.
export const cadencePresetOptions = (t: TFunction) =>
  CADENCE_PRESETS.map(({ value, hours }) => ({
    value,
    label:
      hours % HOURS_PER_WEEK === 0
        ? t("settingsAutomation.duration.weeks", { count: hours / HOURS_PER_WEEK })
        : localizeDuration(t, hours, "hours")
  }))

export const delayPresetFor = (minutes: number): DelayPreset =>
  DELAY_PRESETS.find((preset) => preset.minutes === minutes)?.value ?? CUSTOM

export const cadencePresetFor = (hours: number): CadencePreset =>
  CADENCE_PRESETS.find((preset) => preset.hours === hours)?.value ?? CUSTOM
